import type {OmitStrict} from 'type-zoo'
import {debtCollectorCost} from '@kapitalist/contract/ruleset'
import type {
  TMajorActionDebtCollectorData,
  TPlayerId,
} from '@kapitalist/contract/types'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {skins} from '~/game/skins'
import {DebtCollectorCard} from '~/models/game/Cards/ActionCard'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import {sentenceCase, xVerbY} from '~/utils/language'

type PublicMembersFromApi = OmitStrict<
  TMajorActionDebtCollectorData,
  'card' | 'type'
>

export default class MajorActionDebtCollector
  extends MajorAction<TMajorActionDebtCollectorData>
  implements PublicMembersFromApi
{
  card: DebtCollectorCard
  minorActorPlayerId: TPlayerId

  constructor(data: TMajorActionDebtCollectorData) {
    super(data)
    this.card = new DebtCollectorCard(data.card)
    this.minorActorPlayerId = data.minorActorPlayerId
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const minorActor = gamePosition.players.find(
      o => o.id === this.minorActorPlayerId
    )
    if (!minorActor) {
      throw new Error(
        `Minor actor not found in MajorActionDebtCollector description`
      )
    }

    const dropFirstNoun = tense === 'imperativePrompt'
    const {conjugatedVerb, xNoun, yNoun, possessivePronoun} = xVerbY(
      tense,
      majorActor,
      dropFirstNoun ? 'Charge' : 'charge',
      minorActor,
      currentPlayer?.id
    )

    const actor = dropFirstNoun
      ? undefined
      : xNoun === majorActor.username
        ? xNoun
        : sentenceCase(xNoun)

    return skins[skin].gameLogDescriptions.majorActions.debtCollector({
      tense,
      actor,
      chargeVerb: conjugatedVerb,
      targetName: yNoun,
      cost: show$(debtCollectorCost),
      actorPossessive: possessivePronoun,
      cardName: this.card.displayName(skin),
    })
  }
}
